import {
  UNLEASH_FEATURE_NAME,
  ApiResolver,
  Brand,
  ConsentStatus
} from '@schibsted-nmp/advertising-shared';
import { $config } from '@client/core/atoms/config.js';
import { $consentAtom } from '@client/core/atoms/consent.js';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';

import type { Window } from './Window.js';

import type { Api } from './index.js';

export const { globalPropertyName, unsafePropertyName } =
  ApiResolver.getGlobalPropertyNames('relevantDigital');

export class Resolver extends ApiResolver<
  Api.Uninitialized,
  Api.Initialized,
  Api.Loaded,
  Window
> {
  /**
   * Provides synchronous access to a fully loaded global reference.
   *
   * *Warning: throws if it's not fully loaded!*
   * @see {@linkcode ApiResolver.loadedGlobalRef}.
   */
  get [unsafePropertyName]() {
    return this.loadedGlobalRef;
  }

  protected readonly globalPropertyName = globalPropertyName;

  protected override initialize(
    window: Window.AsUninitialized
  ): asserts window is Window.AsInitialized {
    (window[this.globalPropertyName] ??= {}).cmd ??= [];
  }

  protected override isEnabled() {
    if (!isFeatureEnabled(UNLEASH_FEATURE_NAME.enableGamPrebid)) return false;

    const config = $config.get();
    if (!config?.adServer.prebid) return false;

    // For non-DK brands we don't need user consent to enable Prebid:
    const brandIsDanish = [Brand.Dba, Brand.Bilbasen].includes(config.brand);
    if (!brandIsDanish) return true;

    return $consentAtom.get() === ConsentStatus.Accepted;
  }

  protected override isInitialized(
    window: typeof globalThis.window
  ): window is Window.AsInitialized {
    return window[this.globalPropertyName]?.cmd !== undefined;
  }

  protected override isLoaded(
    globalRef: Api.Initialized | Api.Loaded
  ): globalRef is Api.Loaded {
    return 'loadPrebid' in globalRef;
  }

  protected override enqueue(
    globalRef: Api.Initialized | Api.Loaded,
    executor: () => void
  ) {
    globalRef.cmd.push(executor);
  }
}
